import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG Imports (v18)
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ImageModule } from 'primeng/image';
import { SelectModule } from 'primeng/select'; 

import { emptyOrderAdminKpis, OrderAdminKpisDto, OrderAdminQuery, OrderService } from '../../../../core/services/order.service';
import { Order } from '../../../../shared/models/Order';
import { ImgUrlPipe } from '../../../../shared/pipes/img-url.pipe';
import { PeruDatePipe } from '../../../../shared/pipes/peru-date.pipe';
import { MpPaymentPanel } from '../../../../shared/components/mp-payment-panel/mp-payment-panel';
import { formatMoananiDateOnlyPeru } from '../../../../core/utils/peru-datetime';
import {
  AdminExportBundle,
  boletaMapFromOrders,
  historyBounds,
  loadAdminExport,
  orderIdsWithoutBoletaFlag
} from '../../../../core/utils/admin-order-page';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BoletaService, OrderBoletaStatus } from '../../../../core/services/boleta.service';
import {
  getBoletaExportLabel,
  getOrderAmountPaid,
  getOrderDeliveryPrice,
  getOrderItemsCount,
  getOrderCategoryLabel,
  getOrderModeloLabel,
  getOrderProductLabel,
  getOrderTotalAmount,
  money
} from '../../../../core/utils/order-export';
import { exportWebSalesExcel } from '../../../../core/utils/sales-history-excel';

@Component({
  selector: 'app-web-orders-history',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    TableModule, 
    ButtonModule,
    InputTextModule, 
    TagModule, 
    IconFieldModule, 
    InputIconModule,
    DialogModule, 
    TooltipModule, 
    ToastModule, 
    ImageModule, 
    ImgUrlPipe,
    SelectModule,
    PeruDatePipe,
    MpPaymentPanel,
  ],
  providers: [MessageService],
  templateUrl: './web-orders-history.html',
  styleUrl: './web-orders-history.css',
})
export class WebOrdersHistory implements OnInit {

  filteredHistoryOrders: Order[] = [];
  loading: boolean = true;
  first = 0;
  rows = 10;
  totalRecords = 0;
  search = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  viewDialogVisible: boolean = false;
  selectedOrder: Order | null = null;
  boletaStatusMap: Record<number, OrderBoletaStatus> = {};
  downloadingBoletaOrderId: number | null = null;

  private kpis: OrderAdminKpisDto = emptyOrderAdminKpis();
  private kpiRequest = 0;
  private listRequest = 0;
  exporting: 'excel' | 'pdf' | null = null;
  dateRangeOptions = [
    { label: 'Todo el historial', value: 'all' },
    { label: 'Últimos 7 días', value: '7' },
    { label: 'Últimos 15 días', value: '15' },
    { label: 'Últimos 30 días', value: '30' }
  ];
  selectedRange: string = '30';

  constructor(
    private orderService: OrderService,
    private boletaService: BoletaService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loadKpis();
  }

  scheduleServerLoad(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.reloadFirst(), 350);
  }

  reloadFirst(): void {
    this.first = 0;
    this.loadKpis();
    this.loadHistory({ first: 0, rows: this.rows });
  }

  get totalWebRevenue(): number {
    return this.kpis.cobradoPeriodo;
  }

  get totalWebNeto(): number {
    return this.kpis.netoCobradoEntregadas;
  }

  get totalWebComisiones(): number {
    return this.kpis.comisionesMpEntregadas;
  }

  get totalWebVentas(): number {
    return this.kpis.totalVentasEntregadas;
  }

  get totalWebEnvio(): number {
    return this.kpis.totalEnvioEntregadas;
  }

  private listFilters(): OrderAdminQuery {
    return {
      canal: 'WEB',
      isManualSale: false,
      vista: 'HISTORIAL',
      q: this.search?.trim() || undefined,
      ...historyBounds(this.selectedRange)
    };
  }

  loadKpis(): void {
    const request = ++this.kpiRequest;
    this.orderService.getAdminKpis(this.listFilters()).subscribe({
      next: (kpis) => {
        if (request !== this.kpiRequest) return;
        this.kpis = kpis;
        this.cdr.detectChanges();
      },
      error: () => {
        if (request !== this.kpiRequest) return;
        this.kpis = emptyOrderAdminKpis();
        this.cdr.detectChanges();
      }
    });
  }

  filterByDate() {
    this.reloadFirst();
  }

  loadHistory(event?: TableLazyLoadEvent) {
    const request = ++this.listRequest;
    this.loading = true;
    const first = event?.first ?? this.first;
    const rows = event?.rows || this.rows;
    this.first = first || 0;
    this.rows = rows;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    this.orderService.getAdminOrders({
      ...this.listFilters(),
      page,
      size: this.rows
    }).subscribe({
      next: (res) => {
        if (request !== this.listRequest) return;
        this.filteredHistoryOrders = res.content || [];
        this.totalRecords = res.totalElements || 0;
        this.loading = false;
        this.syncBoletas(this.filteredHistoryOrders);
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (request !== this.listRequest) return;
        this.loading = false;
        this.filteredHistoryOrders = [];
        this.totalRecords = 0;
        this.messageService.add({severity:'error', summary:'Error', detail:'No se pudo cargar el historial.'});
        console.error(err);
        this.cdr.detectChanges();
      }
    });
  }

  private syncBoletas(orders: Order[]): void {
    this.boletaStatusMap = boletaMapFromOrders(orders);
    const missing = orderIdsWithoutBoletaFlag(orders);
    this.cdr.detectChanges();
    if (!missing.length) return;
    this.boletaService.estadosOrdenes(missing).subscribe({
      next: (rows) => {
        rows.forEach(r => { this.boletaStatusMap[r.orderId] = r; });
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  boletaLabel(orderId: number): string {
    const st = this.boletaStatusMap[orderId];
    if (!st?.tieneBoleta) return 'Sin boleta';
    return st.envioCliente === 'ENVIADO' ? 'Enviada' : 'Emitida';
  }

  boletaSeverity(orderId: number): 'success' | 'info' | 'warn' {
    const st = this.boletaStatusMap[orderId];
    if (!st?.tieneBoleta) return 'warn';
    return st.envioCliente === 'ENVIADO' ? 'success' : 'info';
  }

  descargarBoleta(orderId: number): void {
    const st = this.boletaStatusMap[orderId];
    if (!st?.boletaId) return;
    this.downloadingBoletaOrderId = orderId;
    this.boletaService.descargarPdf(st.boletaId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${st.numeroCompleto || 'boleta'}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloadingBoletaOrderId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.downloadingBoletaOrderId = null;
        this.messageService.add({ severity: 'error', summary: 'PDF', detail: 'No se pudo descargar la boleta' });
        this.cdr.detectChanges();
      }
    });
  }

  exportExcel() {
    this.runExport('excel', ({ rows, kpis, boletas }) => exportWebSalesExcel(rows, boletas, {
      cobrado: kpis.cobradoPeriodo,
      ventas: kpis.totalVentasEntregadas,
      envio: kpis.totalEnvioEntregadas
    }));
  }

  exportPdf() {
    this.runExport('pdf', ({ rows, kpis, boletas }) => {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(16);
      doc.text('Reporte Ventas Web', 14, 20);
      doc.setFontSize(10);
      doc.text(`Filtro: ${this.selectedRange === 'all' ? 'Histórico Completo' : 'Últimos ' + this.selectedRange + ' días'}`, 14, 26);

      const body = rows.map(o => [
          o.id,
          formatMoananiDateOnlyPeru(o.createdAt || ''),
          o.clientName,
          getOrderProductLabel(o),
          getOrderCategoryLabel(o),
          getOrderModeloLabel(o),
          o.status,
          money(getOrderDeliveryPrice(o)),
          money(getOrderAmountPaid(o)),
          money(getOrderTotalAmount(o)),
          getBoletaExportLabel(o.id, boletas)
      ]);

      autoTable(doc, {
          startY: 32,
          head: [['N°', 'Fecha', 'Cliente', 'Producto', 'Categoría', 'Modelo', 'Estado', 'Envío', 'Adelanto', 'Total venta', 'Boleta']],
          body: body,
          theme: 'grid',
          headStyles: { fillColor: [0, 0, 0] },
          styles: { fontSize: 6 },
          columnStyles: { 3: { cellWidth: 32 }, 4: { cellWidth: 22 }, 5: { cellWidth: 22 }, 10: { cellWidth: 30 } }
      });

      const finalY = (doc as any).lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total cobrado (adelantos/pagados): ${money(kpis.cobradoPeriodo)}`, 14, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Total ventas: ${money(kpis.totalVentasEntregadas)}  |  Envío (informativo, no suma): ${money(kpis.totalEnvioEntregadas)}`, 14, finalY + 7);

      doc.save('ventas_web.pdf');
    });
  }

  private runExport(
    kind: 'excel' | 'pdf',
    consume: (bundle: AdminExportBundle) => Promise<void> | void
  ): void {
    if (this.exporting) return;
    const summary = kind === 'excel' ? 'Excel' : 'PDF';
    this.exporting = kind;
    this.cdr.detectChanges();
    loadAdminExport(this.orderService, this.boletaService, this.listFilters()).subscribe({
      next: (bundle) => {
        if (!bundle.rows.length) {
          this.exporting = null;
          this.messageService.add({ severity: 'warn', summary, detail: 'No hay pedidos para exportar' });
          this.cdr.detectChanges();
          return;
        }
        Promise.resolve()
          .then(() => consume(bundle))
          .catch((e: Error) => this.messageService.add({
            severity: 'warn',
            summary,
            detail: e?.message || 'No se pudo exportar'
          }))
          .finally(() => {
            this.exporting = null;
            this.cdr.detectChanges();
          });
      },
      error: () => {
        this.exporting = null;
        this.messageService.add({ severity: 'error', summary, detail: 'No se pudo exportar' });
        this.cdr.detectChanges();
      }
    });
  }

  openViewDialog(order: Order) {
    this.selectedOrder = order;
    this.viewDialogVisible = true;
  }

  getSeverity(status: string): "success" | "secondary" | "info" | "warn" | "danger" | "contrast" | undefined {
    switch(status) {
      case 'ENTREGADO': return 'success';
      case 'CANCELADO': return 'danger';
      case 'RECHAZADO': return 'danger';
      case 'PAGO_COMPLETO': return 'success';
      default: return 'info';
    }
  }

  formatStatus(status: string): string {
    return status ? status.replace(/_/g, ' ') : '';
  }
}